import { Cause, Deferred, Effect, Exit, Fiber, Pull, Queue } from 'effect';

/** One producer and one shared cursor, including callers that stop during a pending pull. */
export function createClientEventStream<A, E>(
  produce: (emit: (event: A) => Effect.Effect<void>) => Effect.Effect<void, E>,
  options: {
    signal?: AbortSignal;
    eager?: boolean;
    abortError: () => E;
    restoreError: (error: E) => unknown;
  },
) {
  const queue = Effect.runSync(Queue.bounded<A, E | Cause.Done>(1));
  const stopped = Effect.runSync(Deferred.make<void>());
  let producer: Fiber.Fiber<void> | undefined;
  let closing = false;

  const waitForAbort = options.signal
    ? Effect.callback<never, E>((resume) => {
        const signal = options.signal!;
        const onAbort = () => resume(Effect.fail(options.abortError()));
        signal.addEventListener('abort', onAbort, { once: true });
        if (signal.aborted) onAbort();
        return Effect.sync(() => signal.removeEventListener('abort', onAbort));
      })
    : Effect.never;

  const start = () => {
    if (producer || closing) return;
    producer = Effect.runFork(
      Effect.uninterruptibleMask((restore) =>
        Effect.gen(function* () {
          const exit = yield* Effect.exit(
            restore(
              Effect.suspend(() =>
                options.signal?.aborted
                  ? Effect.fail(options.abortError())
                  : Effect.raceFirst(
                      produce((event) => Queue.offer(queue, event).pipe(Effect.asVoid)),
                      waitForAbort,
                    ),
              ),
            ),
          );
          if (Exit.isFailure(exit)) yield* Queue.failCause(queue, exit.cause);
          else yield* Queue.end(queue);
        }),
      ),
    );
  };

  const close = Effect.uninterruptible(
    Effect.gen(function* () {
      if (closing) return yield* Deferred.await(stopped);
      closing = true;
      if (producer) yield* Fiber.interrupt(producer);
      yield* Queue.shutdown(queue);
      yield* Deferred.succeed(stopped, undefined);
    }),
  );

  const done: IteratorReturnResult<undefined> = { done: true, value: undefined };
  const take = Queue.take(queue).pipe(
    Effect.map((value): IteratorResult<A> => ({ done: false, value })),
    Pull.catchDone(() => Effect.succeed(done)),
  );
  const next = Effect.fn('sdk.eventStream.next')(
    function* (): Effect.fn.Return<IteratorResult<A>, E> {
      if (closing) {
        yield* close;
        return done;
      }
      if (options.signal?.aborted) {
        yield* close;
        return yield* Effect.fail(options.abortError());
      }
      start();
      const result = yield* Effect.exit(take);
      if (closing) {
        yield* close;
        return done;
      }
      if (options.signal?.aborted) {
        yield* close;
        return yield* Effect.fail(options.abortError());
      }
      if (Exit.isFailure(result)) {
        yield* close;
        return yield* Effect.failCause(result.cause);
      }
      if (result.value.done) yield* close;
      return result.value;
    },
    Effect.onInterrupt(() => close),
  );

  const iterator: AsyncGenerator<A> = {
    [Symbol.asyncIterator]() {
      return this;
    },
    next: () => Effect.runPromise(next().pipe(Effect.mapError(options.restoreError))),
    return: async (value) => {
      await Effect.runPromise(close);
      return { done: true, value };
    },
    throw: async (error) => {
      await Effect.runPromise(close);
      throw error;
    },
  };
  if (options.eager) start();
  return { iterator, next, close };
}
